import { PlantItemRenewalFindPage } from 'plantItemRenewal/constants/plantItemRenewalFindPage';
import {
  Col,
  ComponentLoader,
  ErrorSummary,
  FormGroup,
  FormRadioGroup,
  Heading,
  HorizontalRule,
  InPageAlert,
  Modal,
  MoreInfoModal,
  Row,
} from '@snsw/react-component-library';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { StrictInput } from 'shared/components/strictInput/StrictInput';
import { clearApplication, getApplication } from 'shared/redux/applicationSlice';
import { isEmpty, scrollToError, validateExt } from 'shared/utils/Validation';
import { NavigationButtons } from '@onegov/react-component-library';
import { LoadingStates } from 'shared/constants/loadingStates';
import {
  getTangibleDetails,
  storeItemRegistrationNumber,
  storeRenewalNumber,
  validateGracePeriod,
  validateRenewalOrRego,
} from 'plantItemRenewal/redux/renewalItemsSlice';
import { clearPlantItemRenewalForm } from 'plantItemNew/redux/slices/formSlice';
import { store } from 'shared/redux/configureStore';
import { PagePath } from 'shared/constants/pagePath';
import { sanitizeHtml } from 'shared/utils/Helpers';
import { PlantItemRenewalConstants } from 'plantItemRenewal/constants/plantItemRenewalConstants';
import { storePaymentDetails } from 'plantItemRenewal/redux/plantItemRenewalPaymentSlice';

export const PlantItemRenewalFind = () => {
  const PAGE_CONSTANTS = PlantItemRenewalFindPage;
  const POSTCODE_LENGTH = 4;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const plantItemLicenceTypes = useSelector((state) => state.plantItemLicenceTypes);
  const application = useSelector((state) => state.application);
  const renewalItems = useSelector((state) => state.renewalItems);
  const payment = useSelector((state) => state.plantItemRenewalPayment);
  const { paymentReference, paymentRedirectUrl } = payment;

  const [isLoading, setIsLoading] = useState(false);
  const [isInvalidRenewalOrRego, setIsInvalidRenewalOrRego] = useState(false);
  const [invalidRegoOrRenewalErrorTitle, setInvalidRegoOrRenewalErrorTitle] = useState('');
  const [invalidRegoOrRenewalErrorMsg, setInvalidRegoOrRenewalErrorMsg] = useState('');

  const [searchBy, setSearchBy] = useState(renewalItems?.searchBy || '');
  const [renewalNumber, setRenewalNumber] = useState(renewalItems?.renewalNumber || '');
  const [itemRegistrationNumber, setItemRegistrationNumber] = useState(renewalItems?.itemRegistrationNumber || '');
  const [postcode, setPostcode] = useState(renewalItems?.itemLocationPostcode || '');

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);
  const [unAlignedExpiryOpen, setUnAlignedExpiryOpen] = useState(false);
  const [sameUserApplicationInProgress, setSameUserApplicationInProgress] = useState(false);
  const [inProgressApplication, setInProgressApplication] = useState({
    paymentReference: '',
    paymentRedirectUrl: '',
    applicationId: 0,
  });

  let regoOrRenewalError = {
    title: null,
    message: null,
    fieldError: null,
  };

  const onContinueClick = async () => {
    setIsInvalidRenewalOrRego(false);

    const isValid = validateExt(validators, setErrors, setErrorSummary);
    setShowErrors(!isValid);

    if (!isValid) {
      scrollToError();
      return;
    }

    if (searchByRenewalNumber()) dispatch(storeRenewalNumber({ renewalNumber, searchBy }));
    else dispatch(storeItemRegistrationNumber({ itemRegistrationNumber, itemLocationPostcode: postcode, searchBy }));

    setIsLoading(true);
    const renewalOrRegoValidationResult = await store.dispatch(validateRenewalOrRego()).unwrap();

    if (renewalOrRegoValidationResult?.folderRsn) {
      if (renewalOrRegoValidationResult.inProgress) {
        if (
          !renewalOrRegoValidationResult.inProgressSameUser ||
          !renewalOrRegoValidationResult.inProgressPaymentReference
        ) {
          errorRenewalIsInProgress();
        } else {
          setInProgressApplication({
            paymentReference: renewalOrRegoValidationResult.inProgressPaymentReference,
            paymentRedirectUrl: renewalOrRegoValidationResult.inProgressPaymentUrl,
            applicationId: renewalOrRegoValidationResult.inProgressApplicationId,
          });
          setSameUserApplicationInProgress(true);
        }
      } else {
        if (!checkProperty(renewalOrRegoValidationResult)) return;

        await validateGracePeriodFlag();
      }
    } else {
      errorRenewalOrRegoNotFound();
    }

    setIsLoading(false);
  };

  const validateGracePeriodFlag = async () => {
    const gracePeriodFlag = await store.dispatch(validateGracePeriod()).unwrap();

    if (gracePeriodFlag.tooEarlyFlg === 'Y') {
      errorTooEarly();
      return;
    }

    if (gracePeriodFlag.afterGraceFlg === 'Y') {
      errorTooLate();
      return;
    }

    const tangibleDetails = await store.dispatch(getTangibleDetails()).unwrap();

    if (!tangibleDetails?.tangibles?.length) {
      errorCannotBeRenewed();
      return;
    }

    if (gracePeriodFlag.expiryAligned === 'N') {
      setUnAlignedExpiryOpen(true);
    } else {
      navigate(PagePath.plantItemRenewalReviewItems, { replace: true });
    }
  };

  const errorMissingProperty = () => {
    regoOrRenewalError = {
      title: PAGE_CONSTANTS.missingPropertyErrorTitle,
      message: PAGE_CONSTANTS.missingPropertyErrorMessage,
      fieldError: '',
    };

    showInvalidRegoOrRenewalError(regoOrRenewalError);
  };

  const errorCannotBeRenewed = () => {
    regoOrRenewalError = {
      title: PAGE_CONSTANTS.cannotRenewRegoNumberErrorTitle,
      message: PAGE_CONSTANTS.cannotRenewRegoNumberErrorMessage,
      fieldError: PAGE_CONSTANTS.cannotRenewRegoNumberFieldError,
    };

    showInvalidRegoOrRenewalError(regoOrRenewalError);
  };

  const errorRenewalOrRegoNotFound = () => {
    if (searchByRenewalNumber()) {
      regoOrRenewalError = {
        title: PAGE_CONSTANTS.renewalNotFoundErrorTitle,
        message: PAGE_CONSTANTS.renewalNotFoundErrorMessage,
        fieldError: PAGE_CONSTANTS.renewalNotFoundFieldError,
      };
    } else {
      regoOrRenewalError = {
        title: PAGE_CONSTANTS.regoNotFoundErrorTitle,
        message: PAGE_CONSTANTS.regoNotFoundErrorMessage,
        fieldError: '',
      };
    }

    showInvalidRegoOrRenewalError(regoOrRenewalError);
  };

  const errorTooLate = () => {
    if (searchByRenewalNumber()) {
      regoOrRenewalError = {
        title: PAGE_CONSTANTS.tooLateRenewalNumberErrorTitle,
        message: PAGE_CONSTANTS.tooLateRenewalNumberErrorMessage,
        fieldError: PAGE_CONSTANTS.tooLateRenewalNumberFieldError,
      };
    } else {
      regoOrRenewalError = {
        title: PAGE_CONSTANTS.tooLateRegoNumberErrorTitle,
        message: PAGE_CONSTANTS.tooLateRegoNumberErrorMessage,
        fieldError: PAGE_CONSTANTS.tooLateRegoNumberFieldError,
      };
    }

    showInvalidRegoOrRenewalError(regoOrRenewalError);
  };

  const errorTooEarly = () => {
    if (searchByRenewalNumber()) {
      regoOrRenewalError = {
        title: PAGE_CONSTANTS.tooEarlyRenewalNumberErrorTitle,
        message: PAGE_CONSTANTS.tooEarlyRenewalNumberErrorMessage,
        fieldError: PAGE_CONSTANTS.tooEarlyRenewalNumberFieldError,
      };
    } else {
      regoOrRenewalError = {
        title: PAGE_CONSTANTS.tooEarlyRegoNumberErrorTitle,
        message: PAGE_CONSTANTS.tooEarlyRegoNumberErrorMessage,
        fieldError: PAGE_CONSTANTS.tooEarlyRegoNumberFieldError,
      };
    }

    showInvalidRegoOrRenewalError(regoOrRenewalError);
  };

  const errorRenewalIsInProgress = () => {
    if (searchByRenewalNumber()) {
      regoOrRenewalError = {
        title: PAGE_CONSTANTS.inProgressRenewalNumberErrorTitle,
        message: PAGE_CONSTANTS.inProgressRenewalNumberErrorMessage,
        fieldError: PAGE_CONSTANTS.inProgressRenewalNumberFieldError,
      };
    } else {
      regoOrRenewalError = {
        title: PAGE_CONSTANTS.inProgressRegoNumberErrorTitle,
        message: PAGE_CONSTANTS.inProgressRegoNumberErrorMessage,
        fieldError: PAGE_CONSTANTS.inProgressRegoNumberFieldError,
      };
    }

    showInvalidRegoOrRenewalError(regoOrRenewalError);
  };

  const showInvalidRegoOrRenewalError = ({ title, message, fieldError }) => {
    setInvalidRegoOrRenewalErrorTitle(title);
    setInvalidRegoOrRenewalErrorMsg(message);
    setIsInvalidRenewalOrRego(true);

    if (!fieldError) return;

    let error = null;
    if (searchByRenewalNumber()) {
      error = { renewalNumber: { invalid: true, errorMessage: fieldError } };
    } else {
      error = { itemRegistrationNumber: { invalid: true, errorMessage: fieldError } };
    }

    setErrors(error);
  };

  const searchByRenewalNumber = () => {
    return searchBy === PAGE_CONSTANTS.searchByOptions[0].value;
  };

  const searchByItemRegistrationNumber = () => {
    return searchBy === PAGE_CONSTANTS.searchByOptions[1].value;
  };

  const onClickUnalignedExpiryDialog = async () => {
    setUnAlignedExpiryOpen(false);
    navigate(PagePath.plantItemRenewalReviewItems, { replace: true });
  };

  const onClickPayNow = async () => {
    setSameUserApplicationInProgress(false);
    setIsLoading(true);

    //fetch application
    await store.dispatch(getApplication(inProgressApplication.applicationId)).unwrap();

    store.dispatch(
      storePaymentDetails({
        paymentReference: inProgressApplication.paymentReference,
        paymentRedirectUrl: inProgressApplication.paymentRedirectUrl,
      }),
    );

    window.location.href = `${inProgressApplication.paymentRedirectUrl}${inProgressApplication.paymentReference}`;
  };

  const onClickBackToLookup = () => {
    setSameUserApplicationInProgress(false);
    clearData();
  };

  const checkProperty = (validationResult) => {
    if (
      !validationResult?.people?.length ||
      validationResult.people[0]?.type !== PlantItemRenewalConstants.peopleRecordType.organisation
    ) {
      return true;
    }
    if (validationResult.property?.length) return true;

    errorMissingProperty();
    setIsLoading(false);
    return false;
  };

  const validators = [
    {
      fieldName: 'searchBy',
      fieldValue: searchBy,
      isValid: (v) => !isEmpty(v),
      errorMessage: 'You must select an option.',
    },
    {
      fieldName: 'renewalNumber',
      fieldValue: renewalNumber,
      isValid: (v) => !searchByRenewalNumber() || !isEmpty(v),
      errorMessage: 'Renewal number is required.',
    },
    {
      fieldName: 'itemRegistrationNumber',
      fieldValue: itemRegistrationNumber,
      isValid: (v) => !searchByItemRegistrationNumber() || !isEmpty(v),
      errorMessage: 'Item registration number is required.',
    },
    {
      fieldName: 'postcode',
      fieldValue: postcode,
      isValid: (v) => !searchByItemRegistrationNumber() || (!isEmpty(v) && v.length === POSTCODE_LENGTH),
      errorMessage: isEmpty(postcode) ? 'Postcode of item location is required.' : 'Please enter a valid postcode.',
    },
  ];

  const clearData = () => {
    dispatch(clearApplication());
    dispatch(clearPlantItemRenewalForm());
  };

  useEffect(() => {
    clearData();
  }, []);

  useEffect(() => {
    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [searchBy, renewalNumber, itemRegistrationNumber, postcode]);

  return (
    <>
      <Row>
        <Col span={9}>
          {showErrors && !!errorSummary?.length && (
            <ErrorSummary
              singularTitle="We need more information."
              pluralTitle="We need more information."
              errors={errorSummary}
            />
          )}
          {isInvalidRenewalOrRego && (
            <InPageAlert variant="error" title={invalidRegoOrRenewalErrorTitle}>
              {sanitizeHtml(invalidRegoOrRenewalErrorMsg)}
            </InPageAlert>
          )}
        </Col>
      </Row>

      <Heading level={2} style={{ marginBottom: 0, marginTop: 0 }}>
        Renewal lookup
      </Heading>

      <Row>
        <Col span={9}>
          <FormRadioGroup
            id="searchBy"
            name="searchBy"
            vertical={true}
            options={PAGE_CONSTANTS.searchByOptions}
            onChange={(value) => {
              setShowErrors(false);
              setIsInvalidRenewalOrRego(false);
              setSearchBy(value);
            }}
            value={searchBy}
            legend={PAGE_CONSTANTS.searchByLabel}
            helpMessage={PAGE_CONSTANTS.searchByHelpMessage}
            errorMessage={errors?.searchBy?.errorMessage}
            hasError={showErrors && errors?.searchBy?.invalid}
          />

          {searchByRenewalNumber() && (
            <FormGroup
              id="renewalNumber"
              label={PAGE_CONSTANTS.renewalNumberLabel}
              helpMessage={PAGE_CONSTANTS.renewalNumberHelpMessage}
              errorMessage={errors?.renewalNumber?.errorMessage}
              hasError={(showErrors || isInvalidRenewalOrRego) && errors?.renewalNumber?.invalid}
              margin={{ top: 'lg' }}
            >
              <StrictInput
                value={renewalNumber}
                onChange={setRenewalNumber}
                regExpPattern="[^0-9]"
                regExpFlag="g"
                inputWidth="md"
                prefix="PIR-"
              />
            </FormGroup>
          )}

          {searchByItemRegistrationNumber() && (
            <>
              <FormGroup
                id="itemRegistrationNumber"
                label={PAGE_CONSTANTS.itemRegistrationNumberLabel}
                errorMessage={errors?.itemRegistrationNumber?.errorMessage}
                hasError={(showErrors || isInvalidRenewalOrRego) && errors?.itemRegistrationNumber?.invalid}
                margin={{ top: 'lg' }}
              >
                <StrictInput
                  value={itemRegistrationNumber}
                  onChange={setItemRegistrationNumber}
                  regExpPattern="[\s]"
                  regExpFlag="g"
                  inputWidth="md"
                />
              </FormGroup>
              <FormGroup
                id="postcode"
                label={PAGE_CONSTANTS.postcodeLabel}
                errorMessage={errors?.postcode?.errorMessage}
                hasError={showErrors && errors?.postcode?.invalid}
                margin={{ top: 'lg' }}
              >
                <StrictInput
                  value={postcode}
                  onChange={setPostcode}
                  regExpPattern="[^0-9]"
                  regExpFlag="g"
                  maxLength={POSTCODE_LENGTH}
                  inputWidth="sm"
                />
              </FormGroup>
            </>
          )}
        </Col>
      </Row>

      <Row>
        <Col span={9}>
          <Heading level={5} style={{ marginBottom: 0 }}>
            <span style={{ paddingRight: 8 }}>Where can I find these details?</span>
            <MoreInfoModal
              helpText={
                <div style={{ fontWeight: 300 }}>
                  <p>
                    <b>A renewal notice</b> is sent by email or mail 60 days before item(s) expire.
                  </p>
                  <p>
                    If you do not have a renewal notice, you can still renew using the item registration number. You can
                    find this on the <b>registration certificate</b>.{' '}
                  </p>
                  <p>For help, contact SafeWork NSW on 13 10 50.</p>
                </div>
              }
              // questionIconFlag
              subTitle=""
              title="Where can I find these details?"
            />
          </Heading>

          {unAlignedExpiryOpen && (
            <Modal
              title="We’re bringing all items at a plant location into a single renewal."
              buttons={[{ text: 'Close', onClick: onClickUnalignedExpiryDialog }]}
            >
              <p>What this means for you:</p>
              <ul>
                <li>
                  <b>One renewal application</b> for each location.
                </li>
                <li>
                  <b>One expiry date</b> for all items at a location.
                </li>
              </ul>
            </Modal>
          )}
          {sameUserApplicationInProgress && (
            <Modal
              title="Application in progress"
              buttons={[
                { text: 'Pay now', onClick: onClickPayNow },
                { text: 'Back to renewal lookup', onClick: onClickBackToLookup },
              ]}
            >
              You can finalise this renewal by making a payment now.
            </Modal>
          )}
        </Col>
      </Row>

      <HorizontalRule />

      <NavigationButtons
        leftButtonConfig={{
          label: 'Continue',
          onClick: onContinueClick,
        }}
        rightButtonConfig={{
          isVisible: false,
        }}
        backButtonConfig={{
          isVisible: false,
        }}
      />

      {(application.creatingApplication || plantItemLicenceTypes.status === LoadingStates.Loading || isLoading) && (
        <Row>
          <Col span={12}>
            <ComponentLoader fullPage />
          </Col>
        </Row>
      )}
    </>
  );
};
